import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  useEffect(() => {
    setMode(searchParams.get("mode") === "register" ? "register" : "login");
  }, [searchParams]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const next = searchParams.get("next");

  const routeAfter = (user) => {
    if (next) {
      try { navigate(decodeURIComponent(next)); return; }
      catch { /* fall through */ }
    }
    const backendRoles = ["admin", "super_admin", "staff"];
    if (backendRoles.includes(user.role)) navigate("/admin");
    else navigate("/account");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = mode === "register"
        ? await register({ name, email, phone, password })
        : await login(email, password);
      routeAfter(user);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || `${mode === "register" ? "Registration" : "Login"} failed`);
    } finally { setLoading(false); }
  };

  const isReg = mode === "register";

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <div className="flex justify-center mb-6">
        <img src="/brand/fruit-blossoms.png" alt="Fruit Blossoms" className="h-20 w-auto" />
      </div>
      <div className="overline text-zinc-500 mb-3 text-center">Account</div>
      <h1 className="font-display text-4xl font-bold tracking-tight text-center">
        {isReg ? "Create your account" : "Login"}
      </h1>

      <div className="mt-6 flex justify-center">
        <div className="inline-flex rounded-full bg-zinc-100 p-1">
          <button
            type="button"
            onClick={() => setMode("login")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${!isReg ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
            data-testid="auth-tab-login">
            Sign in
          </button>
          <button
            type="button"
            onClick={() => setMode("register")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${isReg ? "bg-[#F74B64] text-white" : "text-zinc-500"}`}
            data-testid="auth-tab-register">
            Create account
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        {isReg && (
          <>
            <div>
              <label className="text-xs font-semibold text-zinc-600 block mb-1">Full name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)}
                     className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="register-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600 block mb-1">Phone / WhatsApp</label>
              <input required value={phone} onChange={(e) => setPhone(e.target.value)}
                     className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="register-phone" />
            </div>
          </>
        )}
        <div>
          <label className="text-xs font-semibold text-zinc-600 block mb-1">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="login-email" />
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-600 block mb-1">Password</label>
          <input type="password" required minLength={isReg ? 6 : undefined}
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 className="w-full border border-zinc-200 rounded-lg px-3 py-2" data-testid="login-password" />
        </div>
        {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">
          {loading ? (isReg ? "Creating…" : "Signing in…") : (isReg ? "Create account" : "Sign in")}
        </button>
      </form>

      {!isReg && (
        <div className="mt-6 text-sm text-zinc-500 text-center">
          New here?{" "}
          <button type="button" onClick={() => setMode("register")}
                  className="text-[#F74B64] font-semibold hover:underline">
            Create an account →
          </button>
        </div>
      )}
      {isReg && (
        <div className="mt-6 text-sm text-zinc-500 text-center">
          Already have one?{" "}
          <button type="button" onClick={() => setMode("login")}
                  className="text-[#F74B64] font-semibold hover:underline">
            Sign in →
          </button>
        </div>
      )}
    </div>
  );
}
